import React, { useState, useEffect, useMemo } from 'react';
import { AllEnumLabels } from '../../utils/enumLabels';

interface DataTableProps<T extends object = object> {
    data: T[];
    onDelete: (id: number | string) => void;
    onEdit?: ((id: string | number) => void | Promise<void>) | ((id: string) => void | Promise<void>);
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (newPage: number) => void;
    selectedId?: string | number;
    idKey?: string | keyof T;
    excludeColumns?: (keyof T)[];
    showIdKeyColumn?: boolean;
    enableFilters?: boolean;
    filterResetKey?: string | number;
    filterableColumns?: string[];
    customCellRenderers?: Record<string, (value: unknown, row: T) => React.ReactNode>;
    renderNestedObjects?: boolean;
}

// ============================================
// Helpers
// ============================================
function isKeyValueObject(value: unknown): boolean {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (keys.length === 0) return false;

    const keysAreShort = keys.every(k => k.length <= 12);
    const hasStringValues = keys.some(k => typeof obj[k] === 'string');
    return keysAreShort && hasStringValues;
}

function getSearchText(_key: string, value: unknown, renderNestedObjects: boolean): string {
    if (value == null) return '';

    if (renderNestedObjects && isKeyValueObject(value)) {
        const obj = value as Record<string, unknown>;
        return Object.entries(obj)
            .map(([k, v]) => `${k} ${String(v ?? '')}`)
            .join(' ');
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>;
        return Object.entries(obj).map(([k, v]) => `${k} ${String(v ?? '')}`).join(' ');
    }

    return String(value);
}

function renderSmartCell(key: string, value: unknown, renderNestedObjects: boolean): React.ReactNode {
    if (value == null) return '—';

    if (renderNestedObjects && isKeyValueObject(value)) {
        const obj = value as Record<string, unknown>;
        const entries = Object.entries(obj);
        if (entries.length === 0) return '—';

        return (
            <div className="flex flex-wrap gap-1.5">
                {entries.map(([k, v]) => {
                    const displayVal = String(v ?? '');
                    return (
                        <span
                            key={k}
                            className="group inline-flex max-w-[240px] items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs transition-all hover:bg-white hover:border-gray-300 hover:shadow-sm"
                            title={`${k}: ${displayVal}`}
                        >
                            <span className="font-mono text-[10px] font-semibold text-indigo-600 shrink-0 tracking-tight">
                                {k}
                            </span>
                            <span className="truncate text-gray-700">
                                {displayVal}
                            </span>
                        </span>
                    );
                })}
            </div>
        );
    }

    const keyLower = key.toLowerCase();

    for (const [enumKey, map] of Object.entries(AllEnumLabels)) {
        if ((keyLower.includes(enumKey) || enumKey.includes(keyLower)) && typeof value === 'number' && map[value] !== undefined) {
            return map[value];
        }
    }

    const isLikelyDateColumn = /createdat|updatedat|created_at|updated_at|date/i.test(keyLower);
    if (isLikelyDateColumn && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
        try {
            return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
        } catch {
            return String(value);
        }
    }

    if (typeof value === 'object' && value !== null) {
        const obj = value as Record<string, unknown>;
        const keys = Object.keys(obj);
        if (keys.length === 0) return '—';
        if (keys.every(k => k.length <= 5)) {
            return keys.map(k => k.toUpperCase()).join(', ') + ` (${keys.length})`;
        }
        return keys.length <= 3 ? keys.join(', ') : `${keys.length} items`;
    }

    return String(value);
}

// ============================================
// DataTable Component
// ============================================
const DataTable = <T extends object = object>({
    data = [],
    onDelete,
    onEdit,
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    selectedId,
    idKey = 'id',
    excludeColumns = [],
    showIdKeyColumn = false,
    enableFilters = false,
    filterResetKey,
    filterableColumns,
    customCellRenderers = {},
    renderNestedObjects = false,
}: DataTableProps<T>) => {
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [keyFilters, setKeyFilters] = useState<Record<string, string>>({}); // ← NEW: For filtering by object keys

    useEffect(() => {
        if (filterResetKey !== undefined) {
            setFilters({});
            setKeyFilters({});
        }
    }, [filterResetKey]);

    const keys = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0])
            .filter((key) => {
                if (key === String(idKey)) return showIdKeyColumn === true;
                return !excludeColumns.includes(key as keyof T);
            }) as (keyof T)[];
    }, [data, idKey, showIdKeyColumn, excludeColumns]);

    // Check if a column contains a key-value object (based on first row)
    const isNestedObjectColumn = (col: string): boolean => {
        if (!renderNestedObjects || !data.length) return false;
        const firstValue = (data[0] as Record<string, unknown>)[col];
        return isKeyValueObject(firstValue);
    };

    const availableKeysPerColumn = useMemo(() => {
        if (!renderNestedObjects || data.length === 0) return {};

        const result: Record<string, string[]> = {};

        keys.forEach((col) => {
            const colName = String(col);
            if (isNestedObjectColumn(colName)) {
                const allKeys = new Set<string>();

                data.forEach((item) => {
                    const value = (item as Record<string, unknown>)[colName];
                    if (isKeyValueObject(value)) {
                        Object.keys(value as Record<string, unknown>).forEach((k) => allKeys.add(k));
                    }
                });

                result[colName] = Array.from(allKeys).sort();
            }
        });

        return result;
    }, [data, keys, renderNestedObjects]);

    const filteredData = useMemo(() => {
        if (!enableFilters) return data;

        return data.filter((item) => {
            const itemRecord = item as Record<string, unknown>;

            // Value filters
            const valueFilterPass = Object.entries(filters).every(([col, filterVal]) => {
                if (!filterVal?.trim()) return true;
                const raw = itemRecord[col];
                const searchText = getSearchText(col, raw, renderNestedObjects);
                return searchText.toLowerCase().includes(filterVal.toLowerCase());
            });

            if (!valueFilterPass) return false;

            // Key filters (only when renderNestedObjects is true)
            const keyFilterPass = Object.entries(keyFilters).every(([col, keyFilterVal]) => {
                if (!keyFilterVal?.trim()) return true;
                const raw = itemRecord[col];
                if (!isKeyValueObject(raw)) return false;

                const objKeys = Object.keys(raw as Record<string, unknown>);
                return objKeys.some(k => k.toLowerCase().includes(keyFilterVal.toLowerCase()));
            });

            return keyFilterPass;
        });
    }, [data, filters, keyFilters, enableFilters, renderNestedObjects]);

    const hasActiveFilters = Object.values(filters).some(v => v?.trim()) || Object.values(keyFilters).some(v => v?.trim());

    const clearAllFilters = () => {
        setFilters({});
        setKeyFilters({});
    };

    const clearFilter = (key: string) => {
        setFilters(prev => { const next = { ...prev }; delete next[key]; return next; });
        setKeyFilters(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    const updateFilter = (key: string, value: string) => setFilters(prev => ({ ...prev, [key]: value }));
    const updateKeyFilter = (key: string, value: string) => setKeyFilters(prev => ({ ...prev, [key]: value }));

    if (!data || data.length === 0) {
        return <p className="text-gray-500 py-8 text-center">No items found.</p>;
    }

    return (
        <div className="-mx-1 overflow-x-auto rounded-lg shadow sm:mx-0">
            {enableFilters && hasActiveFilters && (
                <div className="mb-2 flex justify-end px-1">
                    <button type="button" onClick={clearAllFilters} className="flex min-h-9 items-center gap-1 rounded bg-gray-200 px-3 py-1 text-xs text-gray-600 hover:bg-gray-300">
                        Clear all filters ✕
                    </button>
                </div>
            )}

            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {keys.map(key => (
                            <th key={String(key)} className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6">
                                {String(key).replace(/([A-Z])/g, ' $1').trim()}
                            </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>

                    {enableFilters && (
                        <tr className="bg-gray-100 border-b border-gray-200">
                            {keys.map((key) => {
                                const k = String(key);
                                const canFilter = !filterableColumns || filterableColumns.includes(k);
                                const showKeyFilter = isNestedObjectColumn(k);
                                const availableKeys = availableKeysPerColumn[k] || [];

                                return (
                                    <th key={k} className="px-2 py-2 align-top">
                                        {canFilter ? (
                                            <div className="space-y-1.5">
                                                {/* === KEYS DROPDOWN (only for nested objects) === */}
                                                {showKeyFilter && (
                                                    <div>
                                                        <select
                                                            value={keyFilters[k] || ''}
                                                            onChange={(e) => updateKeyFilter(k, e.target.value)}
                                                            className="w-full text-sm px-3 py-2 border border-indigo-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                        >
                                                            <option value="">All keys</option>
                                                            {availableKeys.map((langKey) => (
                                                                <option key={langKey} value={langKey}>
                                                                    {langKey}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* === VALUE FILTER (bigger) === */}
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        placeholder={`Filter ${k.replace(/([A-Z])/g, ' $1').trim()}...`}
                                                        value={filters[k] || ''}
                                                        onChange={(e) => updateFilter(k, e.target.value)}
                                                        className="w-full text-sm px-3.5 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-9"
                                                    />
                                                    {filters[k] && (
                                                        <button
                                                            onClick={() => clearFilter(k)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-lg leading-none"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="h-9" />
                                        )}
                                    </th>
                                );
                            })}
                            <th className="px-2 py-2"><div className="h-9" /></th>
                        </tr>
                    )}
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.length === 0 ? (
                        <tr>
                            <td colSpan={keys.length + 1} className="px-6 py-8 text-center text-gray-500">No results match your filters.</td>
                        </tr>
                    ) : (
                        filteredData.map((item, index) => {
                            const rowId = (item as Record<string, unknown>)[String(idKey)] ?? index;
                            return (
                                <tr key={String(rowId)} className={`${rowId === selectedId ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                                    {keys.map(key => {
                                        const k = String(key);
                                        const raw = (item as Record<string, unknown>)[k];
                                        const customRenderer = customCellRenderers[k];
                                        const display = customRenderer ? customRenderer(raw, item) : renderSmartCell(k, raw, renderNestedObjects);

                                        return <td key={k} className="px-6 py-4 text-sm text-gray-900 align-top">{display}</td>;
                                    })}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {onEdit && (
                                            <button
                                                onClick={() => {
                                                    const idValue = (item as Record<string, unknown>)[String(idKey)] as string | number;
                                                    (onEdit as (id: string | number) => void | Promise<void>)(idValue);
                                                }}
                                                className="text-blue-600 hover:text-blue-900 font-medium mr-3"
                                            >
                                                Edit
                                            </button>
                                        )}

                                        <button
                                            onClick={() => {
                                                const idValue = (item as Record<string, unknown>)[String(idKey)] as string | number;
                                                onDelete(idValue);
                                            }}
                                            className="text-red-600 hover:text-red-900 font-medium"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>

            {/* Pagination unchanged */}
            {totalPages > 1 && (
                <div className="flex justify-between mt-4 px-2">
                    <button disabled={currentPage === 1} onClick={() => onPageChange?.(currentPage - 1)} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">Previous</button>
                    <span className="self-center text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => onPageChange?.(currentPage + 1)} className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">Next</button>
                </div>
            )}
        </div>
    );
};

export default DataTable;